import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { MessageSquare, Clock, Settings, User } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface NavItem {
  path: string
  icon: typeof MessageSquare
  labelKey: string
}

const navItems: NavItem[] = [
  { path: '/chat', icon: MessageSquare, labelKey: 'nav.chat' },
  { path: '/chat?drawer=history', icon: Clock, labelKey: 'nav.history' },
  { path: '/settings', icon: Settings, labelKey: 'nav.settings' },
  { path: '/profile', icon: User, labelKey: 'nav.profile' },
]

export default function BottomNavigation() {
  const location = useLocation()
  const { t } = useTranslation()
  const [isVisible, setIsVisible] = useState(true)
  const [lastScrollY, setLastScrollY] = useState(0)

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY

      // Always show when at top of page
      if (currentScrollY <= 10) {
        setIsVisible(true)
        setLastScrollY(currentScrollY)
        return
      }

      // Hide when scrolling down, show when scrolling up
      if (currentScrollY > lastScrollY && currentScrollY > 50) {
        setIsVisible(false) // Scrolling down
      } else if (currentScrollY < lastScrollY) {
        setIsVisible(true) // Scrolling up
      }

      setLastScrollY(currentScrollY)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [lastScrollY])

  const isActiveRoute = (path: string) => {
    return location.pathname === path
  }

  return (
    <nav
      className={`fixed bottom-0 left-0 right-0 z-50 md:hidden bg-white border-t border-gray-200 pb-safe-bottom transition-transform duration-300 ease-in-out ${
        isVisible ? 'translate-y-0' : 'translate-y-full'
      }`}
    >
      <div className="flex items-center justify-around h-16">
        {navItems.map((item) => {
          const Icon = item.icon
          const isActive = isActiveRoute(item.path)

          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
                isActive
                  ? 'text-blue-600'
                  : 'text-gray-500 hover:text-gray-700 active:text-gray-900'
              }`}
              style={{
                minWidth: '48px', // Minimum touch target size
                minHeight: '48px',
              }}
            >
              <Icon
                className={`w-6 h-6 ${isActive ? 'stroke-[2.5px]' : 'stroke-2'}`}
              />
              <span
                className={`text-xs mt-1 ${
                  isActive ? 'font-semibold' : 'font-medium'
                }`}
              >
                {t(item.labelKey)}
              </span>
            </Link>
          )
        })}
      </div>
    </nav>
  )
}
